/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Txn } from '@src/app/model/txn';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { BlTxnService } from '@src/app/modules/txn/bl-txn.service';
import { ReconciliationDetailAction } from '@src/app/modules/txn/bank-in-page/reconciliationDetailAction';
import { BankPageNavRoute } from '@src/app/routes/bank-page-nav';
import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { GridField } from '@src/app/shared/grid/grid-field';

/*
* Bank charges, transfers and interest are entered as a shortcut from the bank account screens and
* have no maintenance page of their own. This is a read only drill down so the entry can be examined,
* its audit trail read, and the entry zeroed if it was made in error. There is no menu entry for it,
* it is reached by clicking the transaction in the bank ledger.
*/
@Component({
    selector: 'app-bank-txn-page',
    templateUrl: './bank-txn-page.component.html',
    imports: [FormPageComponent]
})
export class BankTxnPageComponent extends AbstractPageComponent {
    dataSvc = inject(BlTxnService);
    bCodeSvc = inject(BCodeService);
    protected bankAccountSvc = inject(BankAccountService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    dialogRef = inject<MatDialogRef<BankTxnPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });

    readonly path = Txn.LEDGER.BL.code;

    config = new FormConfig({
        navRoute: BankPageNavRoute,
        title: $localize`Bank Transaction`,
        help: $localize`Bank charges, transfers and interest recorded against your bank accounts`,
        itemTitleGetter: (o: Txn) => `${Txn.getType(o.typeId)?.name} ${o.refNr ?? ''}`.trim(),
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, {
                    cellOpts: { heading: 'Ref' }, readonly: true
                }),
                /* No valueObjectName, so the type name comes from Txn.TYPES rather than the server */
                FormPicklistComponent.make('Type', 'typeId', null,
                    { items: Txn.TYPES }, { readonly: true }
                ),
                FormDateComponent.make('Transaction Date', 'txnDate', { readonly: true }),
                FormPicklistComponent.make('Bank Account', 'bankAccountId', null,
                    { service: this.bankAccountSvc }, { formColumn: 2, readonly: true }
                ),
                FormNumberComponent.make('Debit', 'debit',
                    { format: 'currency', width: 9, formatParms: '1.2-2', blankZero: true },
                    { formColumn: 2, readonly: true }
                ),
                FormNumberComponent.make('Credit', 'credit',
                    { format: 'currency', width: 9, formatParms: '1.2-2', blankZero: true },
                    { formColumn: 2, readonly: true }
                ),
                FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
                    { service: this.cycleSvc }, { formColumn: 3, readonly: true, sendServer: false }
                ),
                FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
                    { service: this.periodSvc }, { formColumn: 3, readonly: true, sendServer: false }
                ),
                FieldMaker.notes({ formColumn: 3, readonly: true }),
                FieldMaker.idHolder('bankItemId'),

                /*
                * Always shown - the contra line is where the accountant sees which account the charge
                * or interest went to, and on a transfer it is the bank account the money went to.
                */
                new GridField({
                    field: { label: 'Details', value: 'details', visible: Field.formOnly, formRow: 2 },
                    rowFactory: () => [
                        FieldMaker.id(),
                        FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, {
                            cellOpts: { heading: 'Ref' }, visible: Field.showAll, readonly: true
                        }),
                        FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
                            { service: this.bCodeSvc }, { cellOpts: { width: '40%' }, readonly: true }
                        ),
                        FormPicklistComponent.make('Bank Account', 'bankAccountId', null,
                            { service: this.bankAccountSvc }, { readonly: true }
                        ),
                        FormNumberComponent.make('Debit', 'debit',
                            { format: 'currency', width: 9, formatParms: '1.2-2', blankZero: true },
                            { readonly: true }
                        ),
                        FormNumberComponent.make('Credit', 'credit',
                            { format: 'currency', width: 9, formatParms: '1.2-2', blankZero: true },
                            { readonly: true }
                        ),
                        FieldMaker.notes({ readonly: true }),
                        FieldMaker.rev(),
                    ],
                    hasFooter: false
                }),
            ],
            formLayout: LAYOUT_OPTIONS.threeColOver1,
        }),
        service: this.dataSvc,
        allowEdit: false,
        allowNew: false,
        deleteMsg: Txn.DELETE_MSG,
        deleteReasonNeeded: true,
        /*
        * A matched transaction cannot be deleted, the bank item would be left matched to a nil value
        * transaction. Unreconciling first releases both, so the two actions are offered together.
        */
        actions: [new ReconciliationDetailAction()],
    });
}
